import { useCallback, useEffect, useState } from 'react';
import { sauvegardes } from '../api.js';
import type {
  ComposantSauvegarde,
  MéthodeSauvegarde,
  RésultatRestauration,
  Sauvegarde,
  VérificationDépôt,
} from '../api.js';
import { durée, ilYA, octets } from '../format.js';

interface Props {
  organizationId: string;
  siteId: string;
  siteNom: string;
  rôle: string;
  onRetour: () => void;
}

const LIBELLÉS_TYPE: Record<Sauvegarde['type'], string> = {
  scheduled: 'planifiée',
  manual: 'manuelle',
  pre_update: 'avant mise à jour',
};

const LIBELLÉS_STATUT: Record<Sauvegarde['status'], string> = {
  running: 'en cours',
  success: 'réussie',
  failed: 'en échec',
};

const COMPOSANTS: Array<{ clé: ComposantSauvegarde; libellé: string; aide: string }> = [
  { clé: 'content', libellé: 'Contenu', aide: 'images, thèmes, données de content/' },
  { clé: 'database', libellé: 'Base de données', aide: 'articles, comptes, réglages' },
  { clé: 'config', libellé: 'Configuration', aide: 'config.production.json' },
];

/**
 * Sauvegardes d'un site : historique, déclenchement, vérification et restauration.
 *
 * La restauration est l'opération la plus destructrice de Vigie — elle écrase le contenu en place.
 * L'écran l'entoure donc de trois garde-fous : le choix explicite des composants à restaurer, la
 * saisie du nom du site (le serveur la revérifie, l'interface ne fait que la demander), et un
 * rappel qu'il faut laisser la page ouverte, l'opération étant menée dans la requête.
 */
export function Sauvegardes({ organizationId, siteId, siteNom, rôle, onRetour }: Props) {
  const peutOpérer = rôle === 'admin' || rôle === 'operator';
  const peutRestaurer = rôle === 'admin';

  const [liste, setListe] = useState<Sauvegarde[] | null>(null);
  const [méthode, setMéthode] = useState<MéthodeSauvegarde | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [journaux, setJournaux] = useState<Record<string, string>>({});
  const [vérification, setVérification] = useState<VérificationDépôt | null>(null);

  const [restauration, setRestauration] = useState<string | null>(null);
  const [composants, setComposants] = useState<ComposantSauvegarde[]>([
    'content',
    'database',
    'config',
  ]);
  const [nomSaisi, setNomSaisi] = useState('');
  const [résultat, setRésultat] = useState<RésultatRestauration | null>(null);

  const charger = useCallback(async () => {
    try {
      const { status, data } = await sauvegardes.lister(organizationId, siteId);
      if (status >= 400) {
        setErreur(data.error ?? 'Historique indisponible');
        return;
      }
      setListe(data.backups);
      setMéthode(data.method);
    } catch {
      setErreur('Service injoignable');
    }
  }, [organizationId, siteId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Une sauvegarde tourne en tâche de fond : sans rafraîchissement, l'écran resterait sur
  // « en cours » jusqu'à ce qu'on recharge la page à la main.
  const uneEnCours = liste?.some((sauvegarde) => sauvegarde.status === 'running') ?? false;
  useEffect(() => {
    if (!uneEnCours) return;
    const minuterie = setInterval(() => void charger(), 10_000);
    return () => clearInterval(minuterie);
  }, [uneEnCours, charger]);

  async function lancer() {
    setEnCours('lancement');
    setErreur(null);
    try {
      const { status, data } = await sauvegardes.lancer(organizationId, siteId);
      if (status >= 400) setErreur(data.error ?? 'Lancement impossible');
      else await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function vérifier() {
    setEnCours('vérification');
    setErreur(null);
    setVérification(null);
    try {
      const { status, data } = await sauvegardes.vérifier(organizationId, siteId);
      if (status >= 400) setErreur(data.error ?? 'Vérification impossible');
      else setVérification(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function basculerÉpingle(sauvegarde: Sauvegarde) {
    setEnCours(`epingle-${sauvegarde.id}`);
    setErreur(null);
    try {
      const { status, data } = await sauvegardes.épingler(
        organizationId,
        siteId,
        sauvegarde.id,
        !sauvegarde.pinned,
      );
      if (status >= 400) setErreur(data.error ?? 'Épinglage impossible');
      else await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function afficherJournal(backupId: string) {
    if (journaux[backupId] !== undefined) return;
    try {
      const { status, data } = await sauvegardes.journal(organizationId, siteId, backupId);
      setJournaux((précédents) => ({
        ...précédents,
        [backupId]: status >= 400 ? (data.error ?? 'Journal indisponible') : data.backup.log,
      }));
    } catch {
      setJournaux((précédents) => ({ ...précédents, [backupId]: 'Service injoignable' }));
    }
  }

  async function restaurer(backupId: string) {
    setEnCours('restauration');
    setErreur(null);
    setRésultat(null);
    try {
      const { status, data } = await sauvegardes.restaurer(organizationId, siteId, {
        backupId,
        confirmSiteName: nomSaisi,
        // Tout restaurer se dit en n'envoyant pas de liste : le serveur prend alors les trois.
        ...(composants.length === COMPOSANTS.length ? {} : { components: composants }),
      });
      if (status >= 400) {
        setErreur(data.error ?? 'Restauration impossible');
        return;
      }
      setRésultat(data);
      setRestauration(null);
      setNomSaisi('');
      await charger();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  if (erreur !== null && liste === null) return <p className="erreur">{erreur}</p>;
  if (liste === null) return <p className="aide">Chargement des sauvegardes…</p>;

  return (
    <section className="carte">
      <button type="button" className="lien" onClick={onRetour}>
        ← Retour au site
      </button>
      <h2>Sauvegardes — {siteNom}</h2>

      <p className="aide">
        Rétention : les sauvegardes de plus de 30 jours sont purgées, sauf celles que tu épingles —
        une sauvegarde épinglée est conservée sans limite de temps. À utiliser avant une migration
        ou une refonte : c’est le point de retour qu’on regrette de ne pas avoir gardé.
      </p>
      <p className="aide">
        Méthode : <strong>{méthode ?? 'inconnue'}</strong>{' '}
        {méthode === 'restic'
          ? '— dépôt dédupliqué, restauration par composant et vérification d’intégrité.'
          : méthode === 'streamed'
            ? '— archive chiffrée envoyée en flux vers S3, sans binaire à installer sur le serveur.'
            : ''}
      </p>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      {peutOpérer && (
        <div className="actions">
          <button type="button" disabled={enCours !== null} onClick={() => void lancer()}>
            {enCours === 'lancement' ? 'Mise en file…' : 'Lancer une sauvegarde'}
          </button>
          <button
            type="button"
            className="lien"
            disabled={enCours !== null}
            onClick={() => void vérifier()}
          >
            {enCours === 'vérification' ? 'Vérification…' : 'Vérifier le dépôt'}
          </button>
        </div>
      )}

      {vérification !== null && (
        <div className="carte-interne">
          <h3>Vérification du dépôt</h3>
          {!vérification.supported ? (
            <p className="aide">
              La vérification d’intégrité est propre à restic. En mode streamed, chaque archive est
              chiffrée et scellée : une altération se détecterait au déchiffrement, à la
              restauration.
            </p>
          ) : vérification.ok ? (
            <p className="succes">
              Dépôt sain
              {vérification.snapshots === null
                ? '.'
                : ` — ${String(vérification.snapshots)} instantané${vérification.snapshots > 1 ? 's' : ''}.`}
            </p>
          ) : (
            <p className="erreur">Le dépôt présente des anomalies. Sortie complète ci-dessous.</p>
          )}
          {vérification.output !== '' && (
            <details>
              <summary>Sortie de restic</summary>
              <pre className="journal">{vérification.output}</pre>
            </details>
          )}
        </div>
      )}

      {résultat !== null && (
        <div className="carte-interne">
          <h3>Restauration terminée</h3>
          <p className="succes">
            Composants restaurés : {résultat.restored.join(', ')}
            {résultat.restored.length === 0 && 'aucun'}.
          </p>
          {résultat.restartError !== null && (
            <p className="avertissement">
              Le site n’a pas redémarré proprement : {résultat.restartError}. Les données sont en
              place, mais une intervention est nécessaire pour le relancer.
            </p>
          )}
          <details>
            <summary>Journal de l’opération</summary>
            <pre className="journal">{résultat.log.join('\n')}</pre>
          </details>
        </div>
      )}

      <h3>Historique</h3>
      {liste.length === 0 ? (
        <p className="aide">
          Aucune sauvegarde pour ce site. Le premier passage planifié en créera une, ou lance-en une
          maintenant.
        </p>
      ) : (
        <ul className="liste-sauvegardes">
          {liste.map((sauvegarde) => (
            <li key={sauvegarde.id} className={`sauvegarde sauvegarde--${sauvegarde.status}`}>
              <div className="sauvegarde-entete">
                <span className="sauvegarde-date">
                  {new Date(sauvegarde.startedAt).toLocaleString('fr-FR')}
                </span>
                <span className="etiquette">{LIBELLÉS_TYPE[sauvegarde.type]}</span>
                <span
                  className={
                    sauvegarde.status === 'failed' ? 'etiquette etiquette--alerte' : 'etiquette'
                  }
                >
                  {LIBELLÉS_STATUT[sauvegarde.status]}
                </span>
                {sauvegarde.pinned && (
                  <span className="etiquette" title="Conservée sans limite de temps">
                    épinglée
                  </span>
                )}
                <span className="aide">
                  {octets(sauvegarde.sizeBytes)} ·{' '}
                  {durée(sauvegarde.startedAt, sauvegarde.finishedAt)} ·{' '}
                  {ilYA(sauvegarde.startedAt)}
                </span>
              </div>

              <div className="actions actions--serrees">
                <button
                  type="button"
                  className="lien"
                  onClick={() => void afficherJournal(sauvegarde.id)}
                >
                  Journal
                </button>
                {peutOpérer && sauvegarde.status === 'success' && (
                  <button
                    type="button"
                    className="lien"
                    disabled={enCours !== null}
                    onClick={() => void basculerÉpingle(sauvegarde)}
                    title="Une sauvegarde épinglée échappe à la rétention"
                  >
                    {enCours === `epingle-${sauvegarde.id}`
                      ? '…'
                      : sauvegarde.pinned
                        ? 'Désépingler'
                        : 'Épingler'}
                  </button>
                )}
                {peutRestaurer && sauvegarde.status === 'success' && (
                  <>
                    <a
                      className="lien"
                      href={sauvegardes.urlTéléchargement(
                        organizationId,
                        siteId,
                        sauvegarde.id,
                        'content',
                      )}
                    >
                      Contenu
                    </a>
                    {sauvegarde.method === 'restic' && (
                      <a
                        className="lien"
                        href={sauvegardes.urlTéléchargement(
                          organizationId,
                          siteId,
                          sauvegarde.id,
                          'database',
                        )}
                      >
                        Base
                      </a>
                    )}
                  </>
                )}
                {peutRestaurer && sauvegarde.status === 'success' && (
                  <button
                    type="button"
                    className="lien"
                    onClick={() => {
                      setRestauration(sauvegarde.id);
                      setNomSaisi('');
                      setComposants(['content', 'database', 'config']);
                      setRésultat(null);
                    }}
                  >
                    Restaurer
                  </button>
                )}
              </div>

              {journaux[sauvegarde.id] !== undefined && (
                <pre className="journal">{journaux[sauvegarde.id]}</pre>
              )}

              {restauration === sauvegarde.id && (
                <div className="carte-interne">
                  <h4>Restaurer cette sauvegarde</h4>
                  <p className="avertissement">
                    Cette opération <strong>écrase</strong> les données en place du site « {siteNom}{' '}
                    » et le redémarre. Ce qui a été publié depuis le{' '}
                    {new Date(sauvegarde.startedAt).toLocaleString('fr-FR')} sera perdu.
                  </p>

                  <fieldset className="config-groupe">
                    <legend>Composants à restaurer</legend>
                    {COMPOSANTS.map((composant) => (
                      <div key={composant.clé} className="case">
                        <label>
                          <input
                            type="checkbox"
                            checked={composants.includes(composant.clé)}
                            onChange={(événement) =>
                              setComposants((précédents) =>
                                événement.target.checked
                                  ? [...précédents, composant.clé]
                                  : précédents.filter((clé) => clé !== composant.clé),
                              )
                            }
                          />
                          {composant.libellé} <span className="aide">({composant.aide})</span>
                        </label>
                      </div>
                    ))}
                  </fieldset>

                  <label htmlFor="confirmation-nom">
                    Pour confirmer, saisis le nom du site : <code>{siteNom}</code>
                  </label>
                  <input
                    id="confirmation-nom"
                    type="text"
                    value={nomSaisi}
                    autoComplete="off"
                    onChange={(événement) => setNomSaisi(événement.target.value)}
                  />

                  <p className="aide">
                    La restauration se déroule dans cette requête et peut durer plusieurs minutes :
                    laisse cette page ouverte jusqu’au compte rendu.
                  </p>

                  <div className="actions">
                    <button
                      type="button"
                      disabled={
                        enCours !== null || composants.length === 0 || nomSaisi.trim() !== siteNom
                      }
                      onClick={() => void restaurer(sauvegarde.id)}
                    >
                      {enCours === 'restauration' ? 'Restauration en cours…' : 'Restaurer'}
                    </button>
                    <button
                      type="button"
                      className="lien"
                      disabled={enCours !== null}
                      onClick={() => setRestauration(null)}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
